import { jsx as _jsx } from "nativewind/jsx-runtime";
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, } from 'react';
const EditModeContext = createContext(null);
export function EditModeProvider({ children, isEditMode, initialData, }) {
    const [data, setDataState] = useState(initialData);
    const [undoStack, setUndoStack] = useState([]);
    const [editingRegion, setEditingRegion] = useState(null);
    const pushToUndoStack = useCallback((actionType, previousData) => {
        setUndoStack((prev) => [
            ...prev.slice(-49), // 최대 50개 유지
            { type: actionType, previousData },
        ]);
    }, []);
    const updateData = useCallback((updater) => {
        setDataState((prev) => {
            pushToUndoStack('update', prev);
            return updater(prev);
        });
    }, [pushToUndoStack]);
    const setData = useCallback((newData) => {
        setDataState((prev) => {
            pushToUndoStack('replace', prev);
            return newData;
        });
    }, [pushToUndoStack]);
    const undo = useCallback(() => {
        if (undoStack.length === 0)
            return;
        const lastAction = undoStack[undoStack.length - 1];
        setUndoStack((prev) => prev.slice(0, -1));
        setDataState(lastAction.previousData);
    }, [undoStack]);
    const canUndo = undoStack.length > 0;
    const exportToJson = useCallback(() => {
        return JSON.stringify(data, null, 2);
    }, [data]);
    const importFromJson = useCallback((json) => {
        try {
            const parsed = JSON.parse(json);
            // 기본 유효성 검사
            if (!parsed.id || typeof parsed.id !== 'string') {
                console.error('Invalid JSON: missing or invalid id');
                return false;
            }
            setData(parsed);
            return true;
        }
        catch (error) {
            console.error('Failed to parse JSON:', error);
            return false;
        }
    }, [setData]);
    // === Region 편집 메서드들 ===
    const startAddingRegion = useCallback((routeIndex) => {
        setEditingRegion({
            sectionType: 'route',
            routeIndex,
            regionIndex: null,
            points: [],
            modalImageUrls: [],
            pointsUndoStack: [],
        });
    }, []);
    const startAddingSeatViewRegion = useCallback(() => {
        setEditingRegion({
            sectionType: 'seatView',
            routeIndex: 0,
            regionIndex: null,
            points: [],
            modalImageUrls: [],
            pointsUndoStack: [],
        });
    }, []);
    const startEditingRegion = useCallback((routeIndex, regionIndex, points, modalImageUrls) => {
        setEditingRegion({
            sectionType: 'route',
            routeIndex,
            regionIndex,
            points: [...points],
            modalImageUrls: [...modalImageUrls],
            pointsUndoStack: [],
        });
    }, []);
    const startEditingSeatViewRegion = useCallback((regionIndex, points, modalImageUrls) => {
        setEditingRegion({
            sectionType: 'seatView',
            routeIndex: 0,
            regionIndex,
            points: [...points],
            modalImageUrls: [...modalImageUrls],
            pointsUndoStack: [],
        });
    }, []);
    const addPointToRegion = useCallback((point) => {
        setEditingRegion((prev) => {
            if (!prev)
                return prev;
            return Object.assign(Object.assign({}, prev), { pointsUndoStack: [...prev.pointsUndoStack.slice(-49), prev.points], points: [...prev.points, point] });
        });
    }, []);
    const removePointFromRegion = useCallback((pointIndex) => {
        setEditingRegion((prev) => {
            if (!prev)
                return prev;
            return Object.assign(Object.assign({}, prev), { pointsUndoStack: [...prev.pointsUndoStack.slice(-49), prev.points], points: prev.points.filter((_, i) => i !== pointIndex) });
        });
    }, []);
    const clearRegionPoints = useCallback(() => {
        setEditingRegion((prev) => {
            if (!prev || prev.points.length === 0)
                return prev;
            return Object.assign(Object.assign({}, prev), { pointsUndoStack: [...prev.pointsUndoStack.slice(-49), prev.points], points: [] });
        });
    }, []);
    const undoRegionPoint = useCallback(() => {
        setEditingRegion((prev) => {
            if (!prev || prev.pointsUndoStack.length === 0)
                return prev;
            const previousPoints = prev.pointsUndoStack[prev.pointsUndoStack.length - 1];
            return Object.assign(Object.assign({}, prev), { points: previousPoints, pointsUndoStack: prev.pointsUndoStack.slice(0, -1) });
        });
    }, []);
    const addModalImageToRegion = useCallback((url) => {
        setEditingRegion((prev) => {
            if (!prev)
                return prev;
            return Object.assign(Object.assign({}, prev), { modalImageUrls: [...prev.modalImageUrls, url] });
        });
    }, []);
    const removeModalImageFromRegion = useCallback((index) => {
        setEditingRegion((prev) => {
            if (!prev)
                return prev;
            return Object.assign(Object.assign({}, prev), { modalImageUrls: prev.modalImageUrls.filter((_, i) => i !== index) });
        });
    }, []);
    const saveEditingRegion = useCallback(() => {
        if (!editingRegion)
            return;
        if (editingRegion.points.length < 3) {
            alert('최소 3개의 점이 필요합니다.');
            return;
        }
        const { sectionType, routeIndex, regionIndex, points, modalImageUrls } = editingRegion;
        if (sectionType === 'seatView') {
            // seatViewSection region 저장
            updateData((prev) => {
                var _a;
                if (!((_a = prev.seatViewSection) === null || _a === void 0 ? void 0 : _a.interactiveImage))
                    return prev;
                const currentRegions = prev.seatViewSection.interactiveImage.clickableRegions || [];
                if (regionIndex !== null) {
                    // 기존 region 수정
                    const newRegions = currentRegions.map((region, i) => i === regionIndex ? Object.assign(Object.assign({}, region), { polygon: points, modalImageUrls }) : region);
                    return Object.assign(Object.assign({}, prev), { seatViewSection: Object.assign(Object.assign({}, prev.seatViewSection), { interactiveImage: Object.assign(Object.assign({}, prev.seatViewSection.interactiveImage), { clickableRegions: newRegions }) }) });
                }
                else {
                    // 새 region 추가
                    const newRegion = {
                        id: `region-${Date.now()}`,
                        polygon: points,
                        modalImageUrls,
                    };
                    return Object.assign(Object.assign({}, prev), { seatViewSection: Object.assign(Object.assign({}, prev.seatViewSection), { interactiveImage: Object.assign(Object.assign({}, prev.seatViewSection.interactiveImage), { clickableRegions: [...currentRegions, newRegion] }) }) });
                }
            });
        }
        else {
            // routeSection region 저장
            updateData((prev) => {
                var _a;
                if (!((_a = prev.routeSection) === null || _a === void 0 ? void 0 : _a.routes))
                    return prev;
                const newRoutes = [...prev.routeSection.routes];
                const route = newRoutes[routeIndex];
                if (!(route === null || route === void 0 ? void 0 : route.interactiveImage))
                    return prev;
                const currentRegions = route.interactiveImage.clickableRegions || [];
                if (regionIndex !== null) {
                    // 기존 region 수정
                    const newRegions = currentRegions.map((region, i) => i === regionIndex ? Object.assign(Object.assign({}, region), { polygon: points, modalImageUrls }) : region);
                    newRoutes[routeIndex] = Object.assign(Object.assign({}, route), { interactiveImage: Object.assign(Object.assign({}, route.interactiveImage), { clickableRegions: newRegions }) });
                }
                else {
                    // 새 region 추가
                    const newRegion = {
                        id: `region-${Date.now()}`,
                        polygon: points,
                        modalImageUrls,
                    };
                    newRoutes[routeIndex] = Object.assign(Object.assign({}, route), { interactiveImage: Object.assign(Object.assign({}, route.interactiveImage), { clickableRegions: [...currentRegions, newRegion] }) });
                }
                return Object.assign(Object.assign({}, prev), { routeSection: Object.assign(Object.assign({}, prev.routeSection), { routes: newRoutes }) });
            });
        }
        setEditingRegion(null);
    }, [editingRegion, updateData]);
    const cancelEditingRegion = useCallback(() => {
        setEditingRegion(null);
    }, []);
    const deleteRegion = useCallback((routeIndex, regionIndex) => {
        updateData((prev) => {
            var _a;
            if (!((_a = prev.routeSection) === null || _a === void 0 ? void 0 : _a.routes))
                return prev;
            const newRoutes = [...prev.routeSection.routes];
            const route = newRoutes[routeIndex];
            if (!(route === null || route === void 0 ? void 0 : route.interactiveImage))
                return prev;
            const currentRegions = route.interactiveImage.clickableRegions || [];
            const newRegions = currentRegions.filter((_, i) => i !== regionIndex);
            newRoutes[routeIndex] = Object.assign(Object.assign({}, route), { interactiveImage: Object.assign(Object.assign({}, route.interactiveImage), { clickableRegions: newRegions }) });
            return Object.assign(Object.assign({}, prev), { routeSection: Object.assign(Object.assign({}, prev.routeSection), { routes: newRoutes }) });
        });
    }, [updateData]);
    const deleteSeatViewRegion = useCallback((regionIndex) => {
        updateData((prev) => {
            var _a;
            if (!((_a = prev.seatViewSection) === null || _a === void 0 ? void 0 : _a.interactiveImage))
                return prev;
            const currentRegions = prev.seatViewSection.interactiveImage.clickableRegions || [];
            const newRegions = currentRegions.filter((_, i) => i !== regionIndex);
            return Object.assign(Object.assign({}, prev), { seatViewSection: Object.assign(Object.assign({}, prev.seatViewSection), { interactiveImage: Object.assign(Object.assign({}, prev.seatViewSection.interactiveImage), { clickableRegions: newRegions }) }) });
        });
    }, [updateData]);
    // Cmd+Z 키보드 이벤트 (region 편집 중일 때)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && editingRegion) {
                e.preventDefault();
                undoRegionPoint();
            }
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [editingRegion, undoRegionPoint]);
    // 저장하지 않고 이탈 시 경고
    useEffect(() => {
        if (!isEditMode)
            return;
        const handleBeforeUnload = (e) => {
            if (undoStack.length > 0) {
                e.preventDefault();
                e.returnValue = '저장되지 않은 변경사항이 있습니다. 페이지를 떠나시겠습니까?';
                return e.returnValue;
            }
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => window.removeEventListener('beforeunload', handleBeforeUnload);
        }
    }, [isEditMode, undoStack.length]);
    const value = useMemo(() => ({
        isEditMode,
        data,
        updateData,
        setData,
        undoStack,
        undo,
        canUndo,
        exportToJson,
        importFromJson,
        // Region 편집 관련
        editingRegion,
        startAddingRegion,
        startAddingSeatViewRegion,
        startEditingRegion,
        startEditingSeatViewRegion,
        addPointToRegion,
        removePointFromRegion,
        clearRegionPoints,
        undoRegionPoint,
        addModalImageToRegion,
        removeModalImageFromRegion,
        saveEditingRegion,
        cancelEditingRegion,
        deleteRegion,
        deleteSeatViewRegion,
    }), [
        isEditMode,
        data,
        updateData,
        setData,
        undoStack,
        undo,
        canUndo,
        exportToJson,
        importFromJson,
        editingRegion,
        startAddingRegion,
        startAddingSeatViewRegion,
        startEditingRegion,
        startEditingSeatViewRegion,
        addPointToRegion,
        removePointFromRegion,
        clearRegionPoints,
        undoRegionPoint,
        addModalImageToRegion,
        removeModalImageFromRegion,
        saveEditingRegion,
        cancelEditingRegion,
        deleteRegion,
        deleteSeatViewRegion,
    ]);
    return (_jsx(EditModeContext.Provider, { value: value, children: children }));
}
/**
 * Edit Mode Context 사용 hook
 * @returns EditModeContextValue 또는 null (edit mode가 아닐 때)
 */
export function useEditMode() {
    return useContext(EditModeContext);
}
/**
 * Edit Mode Context 사용 hook (반드시 edit mode일 때만 사용)
 * @throws EditModeProvider 내부가 아닐 때 에러
 */
export function useEditModeRequired() {
    const context = useContext(EditModeContext);
    if (!context) {
        throw new Error('useEditModeRequired must be used within EditModeProvider');
    }
    return context;
}
